import exifr from "exifr";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { ChevronRight, Trash, Globe } from "react-bootstrap-icons"; // Tambahkan Globe
import { createPortal } from "react-dom";
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";

// API base (prefer env; fallback to 3002)
const API_BASE = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");

// constants for Haversine formula
const EARTH_RADIUS_KM = 6371;
const MAX_DISTANCE_METER = 30; // Batas radius 20 meter
const DEFAULT_MAP_CENTER = [-2.548926, 118.0148634];

/**
 * Calculates the great-circle distance between two points on the Earth (Haversine formula).
 * @param {number} lat1 Latitude of point 1 (in degrees)
 * @param {number} lon1 Longitude of point 1 (in degrees)
 * @param {number} lat2 Latitude of point 2 (in degrees)
 * @param {number} lon2 Longitude of point 2 (in degrees)
 * @returns {number} Distance in meters
 */
function haversineDistance(lat1, lon1, lat2, lon2) {
  // Convert degrees to radians
  const toRadian = (angle) => (angle * Math.PI) / 180;

  const dLat = toRadian(lat2 - lat1);
  const dLon = toRadian(lon2 - lon1);
  const radLat1 = toRadian(lat1);
  const radLat2 = toRadian(lat2);

  // Haversine formula
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(radLat1) *
    Math.cos(radLat2) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // Distance in kilometers
  const distanceKm = EARTH_RADIUS_KM * c;

  // Convert to meters
  return distanceKm * 1000;
}

// helpers
function decodeJwtSafe(token) {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const normalized = base64
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const json = atob(normalized);
    return JSON.parse(json);
  } catch {
    return null;
  }
}
const toNumOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const clampLat = (n) =>
  n == null ? null : Math.min(90, Math.max(-90, Number(n)));
const clampLon = (n) =>
  n == null ? null : Math.min(180, Math.max(-180, Number(n)));

function Badge({ children }) {
  return (
    <span
      className="ms-1"
      style={{
        display: "inline-grid",
        placeItems: "center",
        minWidth: 18,
        height: 18,
        padding: "0 6px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        background: "#ef4444",
        color: "white",
      }}
    >
      {children}
    </span>
  );
}

// Image Modal (portal)
function ImageModal({ open, onClose, src }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      document.body.style.overflow = "auto";
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="tp-modal tp-modal-image"
      onClick={(e) => e.target.classList?.contains("tp-modal") && onClose()}
    >
      <div className="tp-card tp-card-image">
        <button className="tp-x" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className="tp-body tp-body-image">
          <img src={src} alt="Full view" className="tp-full-image" />
        </div>
      </div>
      <style>{`
        .tp-modal{position:fixed;inset:0;background:#00000080;display:grid;place-items:center;z-index:2147483647;}
        .tp-card{width:min(640px,94vw);background:#0f1627;color:#e9f1fb;border:1px solid #ffffff24;border-radius:16px;box-shadow:0 18px 60px rgba(0,0,0,.6);}
        .tp-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #ffffff24}
        .tp-title{margin:0;font-size:18px;font-weight:700}
        .tp-x{border:0;background:#ffffff1a;color:#fff;width:32px;height:32px;border-radius:8px;cursor:pointer}
        .tp-x:hover{background:#ffffff2a}
        .tp-body{padding:14px 16px;max-height:70vh;overflow:auto;}
        .tp-foot{display:flex;gap:8px;justify-content:flex-end;padding:14px 16px;border-top:1px solid #ffffff24}
        .list-group-item{background:#0f1627;color:#e9f1fb;border-color:#ffffff24}
        .list-group-item:hover{background:#152037}

        .tp-modal-image { background: rgba(0, 0, 0, 0.9); }
        .tp-card-image { 
          width: min(90vw, 90vh, 1200px); 
          background: none; 
          border: none;
          box-shadow: none;
          position: relative;
        }
        .tp-card-image .tp-x {
          position: absolute;
          top: -40px;
          right: -40px;
          z-index: 10;
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }
        .tp-card-image .tp-x:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        .tp-body-image { 
          padding: 0; 
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .tp-full-image { 
          max-width: 100%; 
          max-height: 90vh; 
          display: block; 
          border-radius: 8px;
        }
      `}</style>
    </div>,
    document.body
  );
}

function AddProjectModal({ open, onClose, onCreate, org }) {
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const [locationName, setLocationName] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    if (!query || query.trim().length < 3) {
      setResults([]);
      if (abortRef.current) abortRef.current.abort();
      return;
    }
    const id = setTimeout(async () => {
      try {
        abortRef.current = new AbortController();
        setSearching(true);
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            query
          )}&limit=5`,
          {
            signal: abortRef.current.signal,
            headers: { "Accept-Language": "en" },
          }
        );
        const data = await r.json();
        setResults(
          (data || []).map((d) => ({
            display: d.display_name,
            lat: d.lat,
            lon: d.lon,
          }))
        );
      } catch {
        // ignore cancelled/failed
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [query, open]);

  useEffect(() => {
    if (!open) {
      setName("");
      setQuery("");
      setLocationName("");
      setLat("");
      setLon("");
      setSearching(false);
      setResults([]);
      if (abortRef.current) abortRef.current.abort();
    }
  }, [open]);

  if (!open) return null;

  const latNum = clampLat(toNumOrNull(lat));
  const lonNum = clampLon(toNumOrNull(lon));
  const canSave = !!org && name.trim().length > 0;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="tp-modal"
      onClick={(e) => e.target.classList?.contains("tp-modal") && onClose()}
    >
      <div className="tp-card" onClick={(e) => e.stopPropagation()}>
        <div className="tp-head">
          <h3 className="tp-title">Add New Project</h3>
          <button className="tp-x" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="tp-body">
          {/* Organization (Read-only) */}
          <div className="mb-3">
            <label className="form-label">Organization</label>
            <input className="form-control" value={org || ""} readOnly />
            <div className="form-text">
              Projects are scoped to your organization.
            </div>
          </div>

          {/* Project Name */}
          <div className="mb-3">
            <label className="form-label">Project Name</label>
            <input
              className="form-control"
              placeholder="e.g., Riyadh Grand Square"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          {/* Helper Text for Coordinates */}
          <div className="form-text mb-2">
            Enter the project's precise coordinates below.
          </div>

          {/* Latitude & Longitude (Primary Input) */}
          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="form-label">Latitude</label>
              <input
                className="form-control"
                placeholder="e.g., 24.7136"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
              />
              {lat && latNum === null && (
                <div className="form-text text-warning">Invalid latitude</div>
              )}
            </div>
            <div className="col-6">
              <label className="form-label">Longitude</label>
              <input
                className="form-control"
                placeholder="e.g., 46.6753"
                value={lon}
                onChange={(e) => setLon(e.target.value)}
              />
              {lon && lonNum === null && (
                <div className="form-text text-warning">Invalid longitude</div>
              )}
            </div>
          </div>

          {/* Location Name */}
          <div className="mb-3">
            <label className="form-label">Location Name</label>
            <input
              className="form-control"
              placeholder="Optional friendly name (e.g., Site Entrance)"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
            />
          </div>

          {/* Visual Separator */}
          <div
            className="my-3"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              color: "#a9b8cf",
            }}
          >
            <hr style={{ flex: 1, borderColor: "#ffffff24" }} />
            <span>OR</span>
            <hr style={{ flex: 1, borderColor: "#ffffff24" }} />
          </div>

          {/* Location Search (Optional) */}
          <div className="mb-2">
            <label className="form-label">
              Find Location by Name (Optional)
            </label>
            <input
              className="form-control"
              placeholder="Search a place to autofill coordinates..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {searching && <div className="form-text">Searching…</div>}
            {!!results.length && (
              <div className="list-group mt-2">
                {results.map((r, i) => (
                  <button
                    key={i}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => {
                      setLocationName(r.display);
                      setLat(r.lat);
                      setLon(r.lon);
                      setResults([]);
                      setQuery("");
                    }}
                  >
                    <i className="bi bi-geo-alt me-2" />
                    {r.display}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="tp-foot">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() =>
              onCreate({
                name: name.trim(),
                location_name: locationName.trim(),
                lat: latNum,
                lon: lonNum,
              })
            }
            disabled={!canSave}
          >
            Save Project
          </button>
        </div>
      </div>

      <style>{`
        .tp-modal{position:fixed;inset:0;background:#00000080;display:grid;place-items:center;z-index:2147483647;}
        .tp-card{width:min(640px,94vw);background:#0f1627;color:#e9f1fb;border:1px solid #ffffff24;border-radius:16px;box-shadow:0 18px 60px rgba(0,0,0,.6);}
        .tp-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #ffffff24}
        .tp-title{margin:0;font-size:18px;font-weight:700}
        .tp-x{border:0;background:#ffffff1a;color:#fff;width:32px;height:32px;border-radius:8px;cursor:pointer}
        .tp-x:hover{background:#ffffff2a}
        .tp-body{padding:14px 16px;max-height:70vh;overflow:auto;}
        .tp-foot{display:flex;gap:8px;justify-content:flex-end;padding:14px 16px;border-top:1px solid #ffffff24}
        .list-group-item{background:#0f1627;color:#e9f1fb;border-color:#ffffff24}
        .list-group-item:hover{background:#152037}
      `}</style>
    </div>,
    document.body
  );
}

// Upload Photos Modal (folder-based, portal)
function UploadPhotosModal({
  open,
  onClose,
  project,
  org,
  token,
  apiBase,
  onUploaded,
  initialFolder = "",
  allFolders = {},
}) {
  const [folder, setFolder] = useState(initialFolder || "");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, busy, onClose]);

  useEffect(() => {
    if (!open) {
      setRows([]);
      setFolder(initialFolder || "");
    } else {
      setFolder(initialFolder || "");
    }
  }, [open, initialFolder]);

  const format6 = (n) =>
    n == null
      ? ""
      : Number(n)
        .toFixed(6)
        .replace(/\.?0+$/, "");

  // 1. Ambil koordinat folder yang relevan
  const targetFolderCoords = useMemo(() => {
    if (!project?.id || !initialFolder || !allFolders[project.id]) return null;

    const folder = allFolders[project.id].find(
      (f) => f.folder_name === initialFolder
    );

    const lat = toNumOrNull(folder?.lat);
    const lon = toNumOrNull(folder?.lon);

    if (lat !== null && lon !== null) {
      return { lat, lon };
    }
    return null;
  }, [project?.id, initialFolder, allFolders]);

  const handlePickFiles = async (files) => {
    if (!files || files.length === 0) return;
    const existingKeys = new Set(
      rows.map((r) => `${r.file.name}::${r.file.size}`)
    );
    const next = [];

    const folderCoords = targetFolderCoords; // Ambil koordinat folder

    for (const file of files) {
      const key = `${file.name}::${file.size}`; // Perbaikan ada di sini: menggunakan file.size
      if (existingKeys.has(key)) continue;

      const base = file.name.replace(/\.[^/.]+$/, "");
      let lat = null,
        lon = null,
        takenAt = null;

      let status = "pending"; // Default status
      let msg = ""; // Default message

      try {
        const { latitude, longitude, DateTimeOriginal } = await exifr.parse(
          file,
          {
            gps: true,
            exif: ["DateTimeOriginal"],
          }
        );
        lat = Number.isFinite(latitude) ? latitude : null;
        lon = Number.isFinite(longitude) ? longitude : null;
        takenAt = DateTimeOriginal || null;

        // --- LOGIKA CEK LOKASI BARU ---
        if (lat !== null && lon !== null && folderCoords) {
          const distance = haversineDistance(
            lat,
            lon,
            folderCoords.lat,
            folderCoords.lon
          );

          if (distance > MAX_DISTANCE_METER) {
            status = "error";
            // Jarak dibulatkan untuk pesan ke user
            const displayDistance = (distance / 1000).toFixed(1);
            msg = `Location out of range! Distance: ${displayDistance} km (> 20m)`;
          }
        } else if (folderCoords && (lat === null || lon === null)) {
          // Jika folder punya koordinat, tapi foto tidak
          status = "error";
          msg =
            "Photo has no GPS data (EXIF) to check against folder location.";
        }
        // --- AKHIR LOGIKA CEK LOKASI BARU ---
      } catch (e) {
        console.warn("Failed to read EXIF data:", e);
        // Jika gagal baca EXIF, ini juga bisa dianggap error (tergantung kebijakan Anda)
        status = "error";
        msg = "Failed to read EXIF or GPS data from photo.";
      }
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        photo_name: base,
        lat,
        lon,
        taken_at: takenAt,
        status: status,
        msg: msg,
      });
    }
    if (next.length === 0) return;
    setRows((r) => [...next, ...r]);
  };

  const uploadAll = async () => {
    if (!project?.id || rows.length === 0 || !folder.trim()) return;
    setBusy(true);
    try {
      const newRows = [...rows];
      for (let i = 0; i < newRows.length; i++) {
        const row = newRows[i];
        // --- PERUBAHAN DI SINI ---
        if (row.status === "done" || row.status === "error") {
          continue; // Lewati foto yang sudah selesai atau error (karena lokasi, dll.)
        }
        // -------------------------

        newRows[i] = { ...row, status: "uploading", msg: "" };
        setRows([...newRows]);

        try {
          const fd = new FormData();
          fd.append("photo_folder", folder.trim());
          fd.append("photo_name", row.photo_name || row.file.name);
          if (row.lat != null) fd.append("lat", String(row.lat));
          if (row.lon != null) fd.append("lon", String(row.lon));
          if (row.taken_at) fd.append("taken_at", row.taken_at.toISOString());
          fd.append("file", row.file);

          const res = await fetch(`${apiBase}/projects/${project.id}/photos`, {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
          });
          if (!res.ok) {
            const t = await res.text();
            throw new Error(t || `Upload failed (${res.status})`);
          }
          const created = await res.json();
          newRows[i] = { ...newRows[i], status: "done", msg: "Uploaded ✓" };
          setRows([...newRows]);
          onUploaded?.(created, project.id);
        } catch (e) {
          newRows[i] = {
            ...newRows[i],
            status: "error",
            msg: e.message || "Upload failed",
          };
          setRows([...newRows]);
        }

        const allDone = newRows.every((row) => row.status === "done");
        if (allDone) {
          onClose(); // Close the modal
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const removeRow = (id) =>
    setRows((rs) => rs.filter((r) => r.id !== id || r.status === "uploading"));

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="tp-modal"
      onClick={(e) =>
        !busy && e.target.classList?.contains("tp-modal") && onClose()
      }
    >
      <div className="tp-card" onClick={(e) => e.stopPropagation()}>
        <div className="tp-head">
          <h3 className="tp-title">Add Photos to Folder</h3>
          <button
            className="tp-x"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
          >
            ×
          </button>
        </div>

        <div className="tp-body">
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label">Project</label>
              <input
                className="form-control"
                value={project?.name || ""}
                readOnly
              />
            </div>
            <div className="col-6">
              <label className="form-label">Organization</label>
              <input className="form-control" value={org || ""} readOnly />
            </div>
          </div>

          {initialFolder ? (
            <>
              <input type="hidden" value={folder} readOnly />
              <div className="mt-2 small" style={{ color: "#a9b8cf" }}>
                Adding to folder: <strong>{initialFolder}</strong> (locked)
              </div>
            </>
          ) : (
            <div className="mt-2">
              <label className="form-label">Photo Folder</label>
              <input
                className="form-control"
                placeholder="e.g., North Tower Crane"
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
              />
              <div className="form-text">
                Photos will be saved under this folder.
              </div>
            </div>
          )}

          <div className="mt-3">
            <label className="form-label">Choose photo(s)</label>
            <input
              type="file"
              accept="image/*,.heic,.heif,.tif,.tiff,.webp"
              multiple
              className="form-control"
              onChange={(e) => handlePickFiles(e.target.files)}
              disabled={busy}
            />
            <div className="form-text">
              GPS will be read from EXIF (fields below are read-only).
            </div>
          </div>

          {rows.length > 0 && (
            <div className="mt-3" aria-live="polite">
              <div className="small mb-2" style={{ color: "#a9b8cf" }}>
                {rows.filter((r) => r.status === "done").length} uploaded,{" "}
                {rows.filter((r) => r.status === "error").length} failed,{" "}
                {
                  rows.filter(
                    (r) => r.status === "pending" || r.status === "uploading"
                  ).length
                }{" "}
                pending
              </div>

              <div className="list-group">
                {rows.map((r) => (
                  <div key={r.id} className="list-group-item">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-image" />
                      <strong className="me-auto text-truncate">
                        {r.file.name}
                      </strong>

                      {r.status === "pending" && (
                        <span className="badge text-bg-secondary">pending</span>
                      )}
                      {r.status === "uploading" && (
                        <span className="badge text-bg-info">uploading…</span>
                      )}
                      {r.status === "done" && (
                        <span className="badge text-bg-success">done</span>
                      )}
                      {r.status === "error" && (
                        <span className="badge text-bg-danger">error</span>
                      )}

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-light"
                        onClick={() => removeRow(r.id)}
                        disabled={r.status === "uploading"}
                        title="Remove from list"
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    </div>

                    <div className="row g-2 mt-2">
                      <div className="col-md-6">
                        <label className="form-label">Photo Name</label>
                        <input
                          className="form-control"
                          value={r.photo_name}
                          onChange={(e) =>
                            setRows((rows) =>
                              rows.map((x) =>
                                x.id === r.id
                                  ? { ...x, photo_name: e.target.value }
                                  : x
                              )
                            )
                          }
                          disabled={
                            r.status === "uploading" || r.status === "done"
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Latitude (EXIF)</label>
                        <input
                          className="form-control"
                          value={format6(r.lat)}
                          readOnly
                          placeholder="—"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Longitude (EXIF)</label>
                        <input
                          className="form-control"
                          value={format6(r.lon)}
                          readOnly
                          placeholder="—"
                        />
                      </div>
                    </div>
                    {r.taken_at && (
                      <div className="row g-2 mt-2">
                        <div className="col-md-12">
                          <label className="form-label">Taken At (EXIF)</label>
                          <input
                            className="form-control"
                            value={new Date(r.taken_at).toLocaleString()}
                            readOnly
                            placeholder="—"
                          />
                        </div>
                      </div>
                    )}

                    {r.msg && (
                      <div
                        className={`small mt-1 ${r.status === "error" ? "text-danger" : "text-success"
                          }`}
                      >
                        {r.msg}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="tp-foot">
          <button
            className="btn btn-outline-light"
            onClick={() => setRows([])}
            disabled={busy || rows.length === 0}
          >
            Clear
          </button>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={uploadAll}
            disabled={
              busy ||
              !folder.trim() ||
              rows.filter((r) => r.status === "pending").length === 0 // Cek apakah ada yang masih 'pending' (siap upload)
            }
          >
            {busy
              ? "Uploading…"
              : `Upload ${rows.length} photo${rows.length > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>

      <style>{`
        .tp-modal{position:fixed;inset:0;background:#00000080;display:grid;place-items:center;z-index:2147483647;}
        .tp-card{width:min(760px,96vw);background:#0f1627;color:#e9f1fb;border:1px solid #ffffff24;border-radius:16px;box-shadow:0 18px 60px rgba(0,0,0,.6);}
        .tp-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #ffffff24}
        .tp-title{margin:0;font-size:18px;font-weight:700}
        .tp-x{border:0;background:#ffffff1a;color:#fff;width:32px;height:32px;border-radius:8px;cursor:pointer}
        .tp-x[disabled]{opacity:.6;cursor:not-allowed}
        .tp-x:hover:not([disabled]){background:#ffffff2a}
        .tp-body{padding:14px 16px;max-height:70vh;overflow:auto;}
        .tp-foot{display:flex;gap:8px;justify-content:flex-end;padding:14px 16px;border-top:1px solid #ffffff24}
        .list-group-item{background:#0f1627;color:#e9f1fb;border-color:#ffffff24}
        .list-group-item:hover{background:#152037}
      `}</style>
    </div>,
    document.body
  );
}

// Data Layer Modal
function AddLayerModal({ open, onClose, onSave, projectId, token, apiBase }) {
  const [layerName, setLayerName] = useState("");
  const [file, setFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setLayerName("");
      setFile(null);
      setIsSaving(false);
      setError("");
    }
  }, [open]);

  const handleSave = async () => {
    if (!layerName || !file || isSaving) return;
    setIsSaving(true);
    setError("");

    const formData = new FormData();
    formData.append("layerName", layerName);
    formData.append("file", file);

    try {
      const res = await fetch(`${apiBase}/projects/${projectId}/layers`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Failed to upload layer (${res.status})`);
      }

      const newLayer = await res.json();
      onSave(newLayer);
    } catch (e) {
      console.error("Failed to save layer:", e);
      setError(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="tp-modal"
      onClick={(e) => e.target.classList?.contains("tp-modal") && onClose()}
    >
      <div className="tp-card" onClick={(e) => e.stopPropagation()}>
        <div className="tp-head">
          <h3 className="tp-title">Add New Data Layer</h3>
          <button
            className="tp-x"
            aria-label="Close"
            onClick={onClose}
            disabled={isSaving}
          >
            ×
          </button>
        </div>
        <div className="tp-body">
          {error && <div className="alert alert-danger mb-3">{error}</div>}
          <div className="mb-3">
            <label htmlFor="layer-name" className="form-label">
              Layer Name
            </label>
            <input
              type="text"
              className="form-control"
              id="layer-name"
              value={layerName}
              onChange={(e) => setLayerName(e.target.value)}
              placeholder="e.g., Site Boundary"
              disabled={isSaving}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="geojson-file" className="form-label">
              GeoJSON File
            </label>
            <input
              type="file"
              className="form-control"
              id="geojson-file"
              accept=".geojson,application/geo+json"
              onChange={(e) => setFile(e.target.files[0])}
              disabled={isSaving}
            />
            <div className="form-text">
              File selected: {file?.name || "No file selected."}
            </div>
          </div>
        </div>
        <div className="tp-foot">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!layerName || !file || isSaving}
          >
            {isSaving ? "Saving..." : "Save Layer"}
          </button>
        </div>
      </div>
      <style>{`
        .tp-modal{position:fixed;inset:0;background:#00000080;display:grid;place-items:center;z-index:2147483647;}
        .tp-card{width:min(500px,94vw);background:#0f1627;color:#e9f1fb;border:1px solid #ffffff24;border-radius:16px;box-shadow:0 18px 60px rgba(0,0,0,.6);}
        .tp-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #ffffff24}
        .tp-title{margin:0;font-size:18px;font-weight:700}
        .tp-x{border:0;background:#ffffff1a;color:#fff;width:32px;height:32px;border-radius:8px;cursor:pointer}
        .tp-x:hover{background:#ffffff2a}
        .tp-body{padding:14px 16px;max-height:70vh;overflow:auto;}
        .tp-foot{display:flex;gap:8px;justify-content:flex-end;padding:14px 16px;border-top:1px solid #ffffff24}
        .alert-danger { background-color: #fca5a5; border-color: #ef4444; color: #7f1d1d; }
      `}</style>
    </div>,
    document.body
  );
}

function formatCoordInput(n) {
  if (n == null) return "";
  return Number(n).toFixed(6).replace(/\.?0+$/, "");
}

function FolderMapViewSync({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    if (!center) return;
    map.setView(center, zoom, { animate: false });
  }, [map, center, zoom]);

  return null;
}

function FolderMapClickLayer({ selectedCenter, projectCenter, onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng);
    },
  });

  return (
    <>
      {projectCenter && (
        <CircleMarker
          center={projectCenter}
          radius={7}
          pathOptions={{
            color: "#8ee6ff",
            fillColor: "#8ee6ff",
            fillOpacity: selectedCenter ? 0.2 : 0.35,
            weight: 2,
          }}
        />
      )}
      {selectedCenter && (
        <CircleMarker
          center={selectedCenter}
          radius={9}
          pathOptions={{
            color: "#1b1f2a",
            fillColor: "#FAB12F",
            fillOpacity: 0.9,
            weight: 2,
          }}
        />
      )}
    </>
  );
}

function FolderMapPicker({ project, lat, lon, onPick, disabled = false }) {
  const projectLat = toNumOrNull(project?.lat);
  const projectLon = toNumOrNull(project?.lon);
  const selectedLat = toNumOrNull(lat);
  const selectedLon = toNumOrNull(lon);

  const projectCenter = useMemo(
    () => (projectLat != null && projectLon != null ? [projectLat, projectLon] : null),
    [projectLat, projectLon]
  );
  const selectedCenter = useMemo(
    () => (selectedLat != null && selectedLon != null ? [selectedLat, selectedLon] : null),
    [selectedLat, selectedLon]
  );

  const mapCenter = selectedCenter || projectCenter || DEFAULT_MAP_CENTER;
  const mapZoom = selectedCenter ? 18 : projectCenter ? 15 : 5;

  return (
    <div className="folder-map-picker mt-3">
      <div className="folder-map-picker__hint">
        Click the map to fill latitude and longitude.
        {projectCenter ? " The blue marker shows the project location." : ""}
      </div>

      <div className={`folder-map-picker__frame${disabled ? " disabled" : ""}`}>
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          scrollWheelZoom
          className="folder-map-picker__map"
          attributionControl={false}
          dragging={!disabled}
          doubleClickZoom={!disabled}
          zoomControl={!disabled}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />
          <FolderMapViewSync center={mapCenter} zoom={mapZoom} />
          <FolderMapClickLayer
            selectedCenter={selectedCenter}
            projectCenter={projectCenter}
            onPick={onPick}
          />
        </MapContainer>
      </div>

      <div className="folder-map-picker__meta">
        {selectedCenter ? (
          <span>
            Selected: {formatCoordInput(selectedLat)}, {formatCoordInput(selectedLon)}
          </span>
        ) : projectCenter ? (
          <span>
            Centered on project: {formatCoordInput(projectLat)},{" "}
            {formatCoordInput(projectLon)}
          </span>
        ) : (
          <span>No coordinates selected yet.</span>
        )}
      </div>
    </div>
  );
}

// Ganti AddFolderModal dengan FolderModal yang lebih komprehensif
function FolderModal({
  open,
  onClose,
  project,
  org,
  token,
  apiBase,
  onFolderCreated,
  onFolderUpdated,
  editingFolder, // New prop for edit mode
}) {
  const [folderName, setFolderName] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingFolder, setLoadingFolder] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, busy, onClose]);

  // Fetch folder data when editing
  useEffect(() => {
    if (open && editingFolder) {
      fetchFolderData();
    } else {
      // Reset form for create mode
      setFolderName("");
      setLat("");
      setLon("");
    }
    if (!open) {
      setShowMapPicker(false);
    }
    setBusy(false);
  }, [open, editingFolder]);

  const fetchFolderData = async () => {
    if (!editingFolder?.id) return;

    setLoadingFolder(true);
    try {
      const res = await fetch(
        `${apiBase}/photo_locations/${editingFolder.id}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch folder data (${res.status})`);
      }

      const folderData = await res.json();

      // Set form data with fetched values
      setFolderName(folderData.folder_name || "");
      setLat(folderData.lat || "");
      setLon(folderData.lon || "");
    } catch (error) {
      console.error("Failed to fetch folder data:", error);
      alert("Failed to load folder data");
    } finally {
      setLoadingFolder(false);
    }
  };

  const handleSaveFolder = async () => {
    if (!project?.id || !folderName.trim() || busy) return;

    const latNum = clampLat(toNumOrNull(lat));
    const lonNum = clampLon(toNumOrNull(lon));

    if (latNum === null || lonNum === null) {
      alert("Please enter valid latitude and longitude");
      return;
    }

    setBusy(true);
    try {
      const payload = {
        folder_name: folderName.trim(),
        lat: latNum,
        lon: lonNum,
      };

      let result;

      if (editingFolder) {
        // EDIT MODE - PUT request
        const res = await fetch(
          `${apiBase}/photo_locations/${editingFolder.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(payload),
          }
        );

        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(errorText || `Failed to update folder (${res.status})`);
        }

        result = await res.json();
        onFolderUpdated?.(result); // Panggil callback untuk update
      } else {
        // CREATE MODE - POST request
        const res = await fetch(
          `${apiBase}/projects/${project.id}/photo-locations`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(payload),
          }
        );

        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(errorText || `Failed to create folder (${res.status})`);
        }

        result = await res.json();
        onFolderCreated?.(result); // Panggil callback untuk create
      }

      onClose();
    } catch (error) {
      console.error("Failed to save folder:", error);
      alert(error.message || `Failed to ${editingFolder ? 'update' : 'create'} folder`);
    } finally {
      setBusy(false);
    }
  };

  const handleMapPick = useCallback(
    (latlng) => {
      if (busy || loadingFolder || !latlng) return;
      setLat(formatCoordInput(clampLat(latlng.lat)));
      setLon(formatCoordInput(clampLon(latlng.lng)));
    },
    [busy, loadingFolder]
  );

  if (!open) return null;

  const isEditMode = !!editingFolder;
  const buttonText = busy
    ? (isEditMode ? "Saving..." : "Creating...")
    : (isEditMode ? "Save Changes" : "Create Folder");
  const modalTitle = isEditMode ? "Edit Folder" : "Create New Folder";
  const projectLat = clampLat(toNumOrNull(project?.lat));
  const projectLon = clampLon(toNumOrNull(project?.lon));
  const projectHasCoords = projectLat != null && projectLon != null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="tp-modal"
      onClick={(e) =>
        !busy && e.target.classList?.contains("tp-modal") && onClose()
      }
    >
      <div className="tp-card" onClick={(e) => e.stopPropagation()}>
        <div className="tp-head">
          <h3 className="tp-title">
            {loadingFolder ? "Loading..." : modalTitle}
          </h3>
          <button
            className="tp-x"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
          >
            ×
          </button>
        </div>

        <div className="tp-body">
          {loadingFolder ? (
            <div className="text-center py-4">
              <div>Loading folder data...</div>
            </div>
          ) : (
            <>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label">Project</label>
                  <input
                    className="form-control"
                    value={project?.name || ""}
                    readOnly
                  />
                </div>
                <div className="col-6">
                  <label className="form-label">Organization</label>
                  <input className="form-control" value={org || ""} readOnly />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Folder Name</label>
                <input
                  className="form-control"
                  placeholder="e.g., North Tower Crane"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  disabled={busy}
                />
              </div>

              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label">Latitude</label>
                  <input
                    className="form-control"
                    placeholder="e.g., -6.893388"
                    value={lat}
                    onChange={(e) => setLat(e.target.value)}
                    disabled={busy}
                  />
                  {lat && toNumOrNull(lat) === null && (
                    <div className="form-text text-warning">Invalid latitude</div>
                  )}
                </div>
                <div className="col-6">
                  <label className="form-label">Longitude</label>
                  <input
                    className="form-control"
                    placeholder="e.g., 107.584976"
                    value={lon}
                    onChange={(e) => setLon(e.target.value)}
                    disabled={busy}
                  />
                  {lon && toNumOrNull(lon) === null && (
                    <div className="form-text text-warning">Invalid longitude</div>
                  )}
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-outline-light btn-sm"
                  onClick={() => setShowMapPicker((v) => !v)}
                  disabled={busy || loadingFolder}
                >
                  <Globe size={14} className="me-2" />
                  {showMapPicker ? "Hide Map Picker" : "Pick on Map"}
                </button>
                {projectHasCoords && (
                  <button
                    type="button"
                    className="btn btn-outline-info btn-sm"
                    onClick={() => {
                      setLat(formatCoordInput(projectLat));
                      setLon(formatCoordInput(projectLon));
                      setShowMapPicker(true);
                    }}
                    disabled={busy || loadingFolder}
                  >
                    Use Project Location
                  </button>
                )}
                {(lat || lon) && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setLat("");
                      setLon("");
                    }}
                    disabled={busy || loadingFolder}
                  >
                    Clear Coordinates
                  </button>
                )}
              </div>

              {showMapPicker && (
                <FolderMapPicker
                  project={project}
                  lat={lat}
                  lon={lon}
                  onPick={handleMapPick}
                  disabled={busy || loadingFolder}
                />
              )}

              <div className="form-text mt-2">
                {isEditMode
                  ? "Updating this folder will also update all photos in this folder."
                  : "This folder will be plotted on the map using the coordinates above."
                }
              </div>
            </>
          )}
        </div>

        <div className="tp-foot">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy || loadingFolder}
          >
            Cancel
          </button>
          <button
            className="btn"
            onClick={handleSaveFolder}
            disabled={
              busy ||
              loadingFolder ||
              !folderName.trim() ||
              !lat ||
              !lon ||
              toNumOrNull(lat) === null ||
              toNumOrNull(lon) === null
            }
            style={{
              backgroundColor: isEditMode ? '#dc3545' : '#0d6efd',
              borderColor: isEditMode ? '#dc3545' : '#0d6efd',
              color: 'white'
            }}
          >
            {buttonText}
          </button>
        </div>
      </div>

      <style>{`
        .tp-modal{position:fixed;inset:0;background:#00000080;display:grid;place-items:center;z-index:2147483647;}
        .tp-card{width:min(500px,94vw);background:#0f1627;color:#e9f1fb;border:1px solid #ffffff24;border-radius:16px;box-shadow:0 18px 60px rgba(0,0,0,.6);}
        .tp-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #ffffff24}
        .tp-title{margin:0;font-size:18px;font-weight:700}
        .tp-x{border:0;background:#ffffff1a;color:#fff;width:32px;height:32px;border-radius:8px;cursor:pointer}
        .tp-x[disabled]{opacity:.6;cursor:not-allowed}
        .tp-x:hover:not([disabled]){background:#ffffff2a}
        .tp-body{padding:14px 16px;max-height:70vh;overflow:auto;}
        .tp-foot{display:flex;gap:8px;justify-content:flex-end;padding:14px 16px;border-top:1px solid #ffffff24}
        .folder-map-picker__hint{font-size:12px;color:#a9b8cf;margin-bottom:8px;}
        .folder-map-picker__frame{border:1px solid #ffffff24;border-radius:12px;overflow:hidden;background:#101827;}
        .folder-map-picker__frame.disabled{opacity:.7;}
        .folder-map-picker__map{height:260px;width:100%;}
        .folder-map-picker__meta{font-size:12px;color:#c7d3e6;margin-top:8px;}
      `}</style>
    </div>,
    document.body
  );
}

// Komponen Modal Baru untuk Panorama
function PanoramaModal({
  open,
  onClose,
  project,
  token,
  apiBase,
  onSave,
  editingPanorama,
}) {
  const [locationName, setLocationName] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isEditMode = !!editingPanorama;

  useEffect(() => {
    if (!open) {
      setLocationName("");
      setLat("");
      setLon("");
      setError("");
    }
  }, [open]);

  useEffect(() => {
    if (isEditMode && editingPanorama) {
      setLocationName(editingPanorama.location_name || "");
      setLat(editingPanorama.lat || "");
      setLon(editingPanorama.lon || "");
    }
  }, [isEditMode, editingPanorama]);

  const handleSave = async () => {
    if (!locationName.trim() || busy) {
      setError("Location name is required.");
      return;
    }

    const latNum = toNumOrNull(lat);
    const lonNum = toNumOrNull(lon);

    setBusy(true);
    setError("");

    try {
      const payload = {
        location_name: locationName.trim(),
        lat: latNum,
        lon: lonNum,
      };

      const url = isEditMode
        ? `${apiBase}/panorama_locations/${editingPanorama.id}`
        : `${apiBase}/projects/${project.id}/panoramas`;

      const method = isEditMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(
          errText ||
          `Failed to ${isEditMode ? "update" : "create"} panorama (${res.status
          })`
        );
      }

      const savedPano = await res.json();
      onSave(savedPano);
      onClose();
    } catch (e) {
      console.error("Failed to save panorama:", e);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="tp-modal"
      onClick={(e) =>
        e.target.classList?.contains("tp-modal") && !busy && onClose()
      }
    >
      <div className="tp-card" onClick={(e) => e.stopPropagation()}>
        <div className="tp-head">
          <h3 className="tp-title">
            {isEditMode ? "Edit Panorama" : "Add New Panorama"}
          </h3>
          <button className="tp-x" aria-label="Close" onClick={onClose} disabled={busy}>
            ×
          </button>
        </div>
        <div className="tp-body">
          {error && <div className="alert alert-danger mb-3">{error}</div>}
          <div className="mb-3">
            <label className="form-label">Location Name</label>
            <input
              className="form-control"
              placeholder="e.g., Main Hallway"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="form-label">Latitude (Optional)</label>
              <input
                className="form-control"
                placeholder="e.g., -6.893388"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="col-6">
              <label className="form-label">Longitude (Optional)</label>
              <input
                className="form-control"
                placeholder="e.g., 107.584976"
                value={lon}
                onChange={(e) => setLon(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>
        </div>
        <div className="tp-foot">
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!locationName.trim() || busy}
          >
            {busy ? "Saving..." : "Save Panorama"}
          </button>
        </div>
      </div>
      <style>{`
        .tp-modal{position:fixed;inset:0;background:#00000080;display:grid;place-items:center;z-index:2147483647;}
        .tp-card{width:min(500px,94vw);background:#0f1627;color:#e9f1fb;border:1px solid #ffffff24;border-radius:16px;box-shadow:0 18px 60px rgba(0,0,0,.6);}
        .tp-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #ffffff24}
        .tp-title{margin:0;font-size:18px;font-weight:700}
        .tp-x{border:0;background:#ffffff1a;color:#fff;width:32px;height:32px;border-radius:8px;cursor:pointer}
        .tp-x:hover{background:#ffffff2a}
        .tp-body{padding:14px 16px;max-height:70vh;overflow:auto;}
        .tp-foot{display:flex;gap:8px;justify-content:flex-end;padding:14px 16px;border-top:1px solid #ffffff24}
        .alert-danger { background-color: #fca5a5; border-color: #ef4444; color: #7f1d1d; }
      `}</style>
    </div>,
    document.body
  );
}

// Komponen Modal baru untuk unggah Foto Panorama
function UploadPanoramaModal({
  open,
  onClose,
  panorama,
  project,
  token,
  apiBase,
  onUploaded,
}) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const isEnabled = !!panorama && !!project;

  const format6 = (n) =>
    n == null
      ? ""
      : Number(n)
        .toFixed(6)
        .replace(/\.?0+$/, "");

  useEffect(() => {
    if (!open) {
      setRows([]);
    }
  }, [open]);

  const handlePickFiles = async (files) => {
    if (!files || files.length === 0) return;
    const existingKeys = new Set(rows.map((r) => `${r.file.name}::${r.file.size}`));
    const next = [];

    const panoCoords = panorama && panorama.lat != null && panorama.lon != null
      ? { lat: toNumOrNull(panorama.lat), lon: toNumOrNull(panorama.lon) }
      : null;

    for (const file of files) {
      const key = `${file.name}::${file.size}`;
      if (existingKeys.has(key)) continue;

      // ✅ VALIDASI: Cek apakah file adalah gambar
      if (!file.type.startsWith('image/')) {
        alert(`File ${file.name} is not a valid image`);
        continue;
      }

      const base = file.name.replace(/\.[^/.]+$/, "");
      let lat = null, lon = null, takenAt = null;
      let status = "pending";
      let msg = "";

      try {
        const { latitude, longitude, DateTimeOriginal } = await exifr.parse(file, {
          gps: true,
          exif: ["DateTimeOriginal"],
        });
        lat = Number.isFinite(latitude) ? latitude : null;
        lon = Number.isFinite(longitude) ? longitude : null;
        takenAt = DateTimeOriginal || null;

        if (lat !== null && lon !== null && panoCoords) {
          const distance = haversineDistance(lat, lon, panoCoords.lat, panoCoords.lon);

          if (distance > MAX_DISTANCE_METER) {
            status = "error";
            const displayDistance = (distance / 1000).toFixed(1);
            msg = `Location out of range! Distance: ${displayDistance} km (> 30m)`;
          }
        } else if (panoCoords && (lat === null || lon === null)) {
          status = "error";
          msg = "Photo has no GPS (EXIF) data to check against folder location.";
        }
      } catch (e) {
        console.warn("Failed to read EXIF data:", e);
        if (panoCoords) {
          status = "error";
          msg = "Failed to read EXIF or GPS data from photo.";
        }
      }

      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        photo_name: base,
        lat,
        lon,
        taken_at: takenAt,
        status: status,
        msg: msg,
      });
    }
    if (next.length > 0) {
      setRows((r) => [...next, ...r]);
    }
  };

  // const handlePickFiles = async (files) => {
  //   if (!files || files.length === 0) return;
  //   const existingKeys = new Set(rows.map((r) => `${r.file.name}::${r.file.size}`));
  //   const next = [];

  //   for (const file of files) {
  //     const key = `${file.name}::${file.size}`;
  //     if (existingKeys.has(key)) continue;
  //     const base = file.name.replace(/\.[^/.]+$/, "");

  //     next.push({
  //       id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  //       file,
  //       photo_name: base,
  //       status: "pending",
  //       msg: "",
  //     });
  //   }
  //   if (next.length > 0) {
  //     setRows((r) => [...next, ...r]);
  //   }
  // };

  const uploadAll = async () => {
    if (!isEnabled || rows.length === 0 || busy) return;
    setBusy(true);
    try {
      const newRows = [...rows];
      for (let i = 0; i < newRows.length; i++) {
        const row = newRows[i];
        if (row.status === "done" || row.status === "error") {
          continue;
        }
        newRows[i] = { ...row, status: "uploading", msg: "" };
        setRows([...newRows]);

        try {
          const fd = new FormData();

          fd.append("photo_name", row.photo_name || row.file.name);
          fd.append("file", row.file);
          fd.append("photo_folder", panorama.location_name);
          if (row.lat != null) fd.append("lat", String(row.lat));
          if (row.lon != null) fd.append("lon", String(row.lon));
          if (row.taken_at) fd.append("taken_at", row.taken_at.toISOString());

          const res = await fetch(`${apiBase}/panorama_locations/${panorama.id}/photos`, {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: fd,
          });

          if (!res.ok) {
            const t = await res.text();
            throw new Error(t || `Upload failed (${res.status})`);
          }
          const created = await res.json();
          newRows[i] = { ...newRows[i], status: "done", msg: "Uploaded ✓" };
          setRows([...newRows]);
          onUploaded?.(created, panorama.id);
        } catch (e) {
          newRows[i] = {
            ...newRows[i],
            status: "error",
            msg: e.message || "Upload failed",
          };
          setRows([...newRows]);
        }
      }
      const allDone = newRows.every(row => row.status === "done" || row.status === "error");
      if (allDone) {
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id || r.status === "uploading"));

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="tp-modal"
      onClick={(e) => !busy && e.target.classList?.contains("tp-modal") && onClose()}
    >
      <div className="tp-card" onClick={(e) => e.stopPropagation()}>
        <div className="tp-head">
          <h3 className="tp-title">Add Photos to Panorama Location</h3>
          <button className="tp-x" aria-label="Close" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="tp-body">
          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="form-label">Project</label>
              <input className="form-control" value={project?.name || ""} readOnly />
            </div>
            <div className="col-6">
              <label className="form-label">Panorama Location</label>
              <input className="form-control" value={panorama?.location_name || ""} readOnly />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Choose panorama photo(s)</label>
            <input
              type="file"
              accept="image/jpeg"
              multiple
              className="form-control"
              onChange={(e) => handlePickFiles(e.target.files)}
              disabled={busy}
            />
            <div className="form-text">
              Recommended format: JPG/JPEG.
            </div>
          </div>

          {rows.length > 0 && (
            <div className="mt-3" aria-live="polite">
              <div className="list-group">
                {rows.map((r) => (
                  <div key={r.id} className="list-group-item">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-image" />
                      <strong className="me-auto text-truncate">{r.file.name}</strong>
                      {r.status === "pending" && (<span className="badge text-bg-secondary">pending</span>)}
                      {r.status === "uploading" && (<span className="badge text-bg-info">uploading…</span>)}
                      {r.status === "done" && (<span className="badge text-bg-success">done</span>)}
                      {r.status === "error" && (<span className="badge text-bg-danger">error</span>)}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-light"
                        onClick={() => removeRow(r.id)}
                        disabled={r.status === "uploading"}
                        title="Remove from list">
                        <i className="bi bi-x-lg" />
                      </button>
                    </div>
                    <div className="row g-2 mt-2">
                      <div className="col-12">
                        <label className="form-label">Photo Name</label>
                        <input
                          className="form-control"
                          value={r.photo_name}
                          onChange={(e) =>
                            setRows((rows) =>
                              rows.map((x) =>
                                x.id === r.id ? { ...x, photo_name: e.target.value } : x
                              )
                            )
                          }
                          disabled={r.status === "uploading" || r.status === "done"}
                        />
                      </div>
                    </div>

                    <div className="row g-2 mt-2">
                      <div className="col-md-4">
                        <label className="form-label">Latitude (EXIF)</label>
                        <input
                          className="form-control"
                          value={format6(r.lat)}
                          readOnly
                          placeholder="—"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Longitude (EXIF)</label>
                        <input
                          className="form-control"
                          value={format6(r.lon)}
                          readOnly
                          placeholder="—"
                        />
                      </div>
                      {r.taken_at && (
                        <div className="col-md-4">
                          <label className="form-label">Taken At (EXIF)</label>
                          <input
                            className="form-control"
                            value={new Date(r.taken_at).toLocaleString()}
                            readOnly
                            placeholder="—"
                          />
                        </div>
                      )}
                    </div>

                    {r.msg && (
                      <div className={`small mt-1 ${r.status === "error" ? "text-danger" : "text-success"}`}>
                        {r.msg}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="tp-foot">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={uploadAll}
            disabled={busy || rows.filter(r => r.status === "pending").length === 0}
          >
            {busy ? "Uploading..." : `Upload ${rows.length} photo${rows.length > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
      <style>{`
        .tp-modal{position:fixed;inset:0;background:#00000080;display:grid;place-items:center;z-index:2147483647;}
        .tp-card{width:min(760px,96vw);background:#0f1627;color:#e9f1fb;border:1px solid #ffffff24;border-radius:16px;box-shadow:0 18px 60px rgba(0,0,0,.6);}
        .tp-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #ffffff24}
        .tp-title{margin:0;font-size:18px;font-weight:700}
        .tp-x{border:0;background:#ffffff1a;color:#fff;width:32px;height:32px;border-radius:8px;cursor:pointer}
        .tp-x[disabled]{opacity:.6;cursor:not-allowed}
        .tp-x:hover:not([disabled]){background:#ffffff2a}
        .tp-body{padding:14px 16px;max-height:70vh;overflow:auto;}
        .tp-foot{display:flex;gap:8px;justify-content:flex-end;padding:14px 16px;border-top:1px solid #ffffff24}
        .list-group-item{background:#0f1627;color:#e9f1fb;border-color:#ffffff24}
        .list-group-item:hover{background:#152037}
      `}</style>
    </div>,
    document.body
  );
}

// =============================================================
// Main Sidebar Component
// =============================================================

export default function Sidebar({
  onLayersChanged,
  onPhotoDataChanged,
  activeLayerIds,
  toggleLayerVisibility,
  folders,
  foldersLoading,
  refreshFolders,
  panoramas,
  panoramasLoading,
  refreshPanoramas,
  onMapFocusRequest,
}) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const currentProjectIdInUrl = useMemo(
    () => new URLSearchParams(search).get("projectId"),
    [search]
  );

  const token = localStorage.getItem("token") || "";
  const decoded = token ? decodeJwtSafe(token) : null;
  const ORG = (
    localStorage.getItem("organization") ||
    decoded?.organization ||
    ""
  ).trim();

  const displayName =
    (localStorage.getItem("name") || decoded?.name || "").trim() ||
    (decoded?.email ? decoded.email.split("@")[0] : "User");

  const displayEmail =
    (localStorage.getItem("email") ||
      decoded?.email ||
      localStorage.getItem("rememberEmail") ||
      "") + "";

  const profileImg = localStorage.getItem("profileImage") || "";
  const initials =
    (displayName || displayEmail || "U").trim()[0]?.toUpperCase() || "U";

  const HOME_PATHS = ["/Homepage", "/Updates", "/reports"];
  const [homeOpen, setHomeOpen] = useState(() =>
    HOME_PATHS.some((p) => pathname.startsWith(p))
  );
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [userOpen, setUserOpen] = useState(false);

  const [projects, setProjects] = useState([]);
  const [photoLocs, setPhotoLocs] = useState({});
  const [dataLayers, setDataLayers] = useState({});
  //const [panoramas, setPanoramas] = useState({}); // State baru untuk panorama
  const [openFolders, setOpenFolders] = useState({});
  const [loading, setLoading] = useState(false);
  const [photosLoading, setPhotosLoading] = useState({});
  const [layersLoading, setLayersLoading] = useState({});
  //const [panoramasLoading, setPanoramasLoading] = useState({}); // State baru untuk loading panorama
  const [err, setErr] = useState("");

  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [addPhotoFor, setAddPhotoFor] = useState(null);
  const [imageViewFor, setImageViewFor] = useState(null);
  const [addLayerOpen, setAddLayerOpen] = useState(false);
  const [addLayerFor, setAddLayerFor] = useState(null);

  const [hoveredProject, setHoveredProject] = useState(null);
  const [hoveredPhoto, setHoveredPhoto] = useState(null);
  const [hoveredLayer, setHoveredLayer] = useState(null);
  const isProjectPage = pathname.startsWith("/Project");

  // State baru untuk folders
  //const [folders, setFolders] = useState({}); // { projectId: [folder1, folder2, ...] }
  // [foldersLoading, setFoldersLoading] = useState({});
  const [addFolderOpen, setAddFolderOpen] = useState(false);
  const [addFolderFor, setAddFolderFor] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);

  // State untuk panorama
  const [addPanoramaOpen, setAddPanoramaOpen] = useState(false);
  const [addPanoramaFor, setAddPanoramaFor] = useState(null);
  const [editingPanorama, setEditingPanorama] = useState(null);
  const [uploadPanoramaFor, setUploadPanoramaFor] = useState(null);
  const [panoramaPhotos, setPanoramaPhotos] = useState({}); // State baru untuk foto-foto panorama


  const signOut = useCallback(() => {
    const remembered = localStorage.getItem("rememberEmail");
    localStorage.clear();
    if (remembered) localStorage.setItem("rememberEmail", remembered);
    navigate("/");
  }, [navigate]);

  const handleUnauthorized = useCallback(async (res) => {
    const data = await res.json().catch(() => ({}));
    const tokenIsExpired =
      data.code === "TOKEN_EXPIRED" ||
      (decoded?.exp && decoded.exp * 1000 <= Date.now());

    if (tokenIsExpired) {
      signOut();
      return;
    }

    // Keep a fresh-looking session intact when an API instance rejects its
    // signature (usually mismatched JWT_SECRET values during deployment).
    setErr(data.message || "The server rejected this session. Please retry after the API is restarted.");
  }, [decoded?.exp, signOut]);

  const groupByFolder = (rows) => {
    const grouped = {};
    (rows || []).forEach((r) => {
      const key = (r.photo_folder || "(Uncategorized)").trim();
      grouped[key] = grouped[key] || [];
      grouped[key].push(r);
    });
    return grouped;
  };

  // load photo locations (per project)
  const loadPhotoLocations = useCallback(
    async (project) => {
      try {
        setPhotosLoading((m) => ({ ...m, [project.id]: true }));
        const res = await fetch(`${API_BASE}/projects/${project.id}/photos`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: "application/json",
          },
        });
        if (res.status === 401) {
          await handleUnauthorized(res);
          return;
        }
        if (!res.ok)
          throw new Error(`Failed to load photo locations (${res.status})`);
        const data = await res.json();

        const photosWithProjectId = Array.isArray(data)
          ? data.map((photo) => ({ ...photo, project_id: project.id }))
          : [];

        setPhotoLocs((m) => ({
          ...m,
          [project.id]: groupByFolder(photosWithProjectId),
        }));
      } catch (e) {
        setErr(e.message || "Failed to load photo locations");
      } finally {
        setPhotosLoading((m) => ({ ...m, [project.id]: false }));
      }
    },
    [token, handleUnauthorized]
  );

  // load data layers
  const loadDataLayers = useCallback(
    async (projectId) => {
      if (!projectId) return;

      try {
        setLayersLoading((m) => ({ ...m, [projectId]: true }));
        const res = await fetch(`${API_BASE}/projects/${projectId}/layers`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (res.status === 401) {
          await handleUnauthorized(res);
          return;
        }
        if (!res.ok)
          throw new Error(
            `Failed to load data layers for project ${projectId}`
          );
        const data = await res.json();
        const layers = Array.isArray(data) ? data : [];
        setDataLayers((m) => ({ ...m, [projectId]: layers }));
        // Panggil onLayersChanged dengan data layer
        onLayersChanged({ type: "load", projectId, layers });
      } catch (e) {
        setErr(e.message || "Failed to load data layers");
      } finally {
        setLayersLoading((m) => ({ ...m, [projectId]: false }));
      }
    },
    [token, handleUnauthorized, onLayersChanged]
  );

  // Fungsi baru untuk memuat panorama
  // const loadPanoramas = useCallback(
  //   async (project) => {
  //     try {
  //       setPanoramasLoading((m) => ({ ...m, [project.id]: true }));
  //       const res = await fetch(`${API_BASE}/projects/${project.id}/panoramas`, {
  //         headers: {
  //           ...(token ? { Authorization: `Bearer ${token}` } : {}),
  //           Accept: "application/json",
  //         },
  //       });
  //       if (res.status === 401) {
  //         signOut();
  //         return;
  //       }
  //       if (!res.ok)
  //         throw new Error(`Failed to load panoramas (${res.status})`);
  //       const data = await res.json();
  //       setPanoramas((m) => ({ ...m, [project.id]: data }));
  //     } catch (e) {
  //       setErr(e.message || "Failed to load panoramas");
  //     } finally {
  //       setPanoramasLoading((m) => ({ ...m, [project.id]: false }));
  //     }
  //   },
  //   [token, signOut]
  // );

  // Fungsi baru untuk memuat foto-foto di dalam sebuah panorama location
  const loadPanoramaPhotos = useCallback(
    async (panoId) => {
      try {
        const res = await fetch(`${API_BASE}/panorama_locations/${panoId}/photos`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (res.status === 401) {
          await handleUnauthorized(res);
          return;
        }
        if (!res.ok) {
          throw new Error(`Failed to load panorama photos (${res.status})`);
        }
        const data = await res.json();
        setPanoramaPhotos(prev => ({
          ...prev,
          [panoId]: data
        }));
      } catch (e) {
        console.error("Error loading panorama photos:", e);
        setErr(e.message || "Failed to load panorama photos");
      }
    },
    [token, handleUnauthorized]
  );

  // load projects
  const loadProjects = useCallback(async () => {
    try {
      setErr("");
      setLoading(true); // Ambil semua data proyek tanpa filter URL
      const res = await fetch(`${API_BASE}/projects/`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
        },
      });
      if (res.status === 401) {
        await handleUnauthorized(res);
        return;
      }
      if (!res.ok) throw new Error(`Failed to load projects (${res.status})`);
      const allProjects = await res.json();
      setProjects(Array.isArray(allProjects) ? allProjects : []);
    } catch (e) {
      setErr(e.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [token, handleUnauthorized]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (!projects || projects.length === 0) return;
    projects.forEach((p) => {
      if (!photoLocs[p.id] && !photosLoading[p.id]) {
        loadPhotoLocations(p);
      }
      if (!dataLayers[p.id] && !layersLoading[p.id]) {
        loadDataLayers(p.id);
      }

    });
  }, [
    projects,
    photoLocs,
    photosLoading,
    loadPhotoLocations,
    dataLayers,
    layersLoading,
    loadDataLayers,


  ]);

  // create project
  const handleCreateProject = async (payload) => {
    try {
      setErr("");
      if (!ORG) throw new Error("No organization found for the current user.");
      const body = {
        name: payload.name,
        location_name: payload.location_name || "",
        lat: payload.lat ?? null,
        lon: payload.lon ?? null,
        organization: ORG,
      };
      const res = await fetch(`${API_BASE}/projects/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        await handleUnauthorized(res);
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Failed to create project (${res.status})`);
      }
      const created = await res.json();
      setProjects((p) => [created, ...p]);
      setAddProjectOpen(false);
    } catch (e) {
      setErr(e.message || "Failed to create project");
    }
  };

  // delete photo functions
  const handleDeletePhoto = useCallback(
    async (photoToDelete) => {
      if (
        !window.confirm(
          `Are you sure you want to delete photo "${photoToDelete.photo_name}"?`
        )
      ) {
        return;
      }
      try {
        setErr("");
        const res = await fetch(
          `${API_BASE}/projects/${photoToDelete.project_id}/photos/${photoToDelete.id}`,
          {
            method: "DELETE",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.status === 401) {
          await handleUnauthorized(res);
          return;
        }
        if (!res.ok) throw new Error(`Failed to delete photo (${res.status})`);

        setPhotoLocs((m) => {
          const currentProjectPhotos = m[photoToDelete.project_id] || {};
          const updatedFolderPhotos = (
            currentProjectPhotos[photoToDelete.photo_folder] || []
          ).filter((photo) => photo.id !== photoToDelete.id);
          return {
            ...m,
            [photoToDelete.project_id]: {
              ...currentProjectPhotos,
              [photoToDelete.photo_folder]: updatedFolderPhotos,
            },
          };
        });

        onPhotoDataChanged();
      } catch (e) {
        setErr(e.message || "Failed to delete photo");
      }
    },
    [token, handleUnauthorized, onPhotoDataChanged]
  );

  const handleDeleteProject = useCallback(
    async (projectToDelete) => {
      if (
        !window.confirm(
          `Are you sure you want to delete the project "${projectToDelete.name}"? This action cannot be undone.`
        )
      ) {
        return;
      }
      try {
        setErr("");
        const res = await fetch(`${API_BASE}/projects/${projectToDelete.id}`, {
          method: "DELETE",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.status === 401) {
          await handleUnauthorized(res);
          return;
        }
        if (!res.ok)
          throw new Error(`Failed to delete project (${res.status})`);

        setProjects((prevProjects) =>
          prevProjects.filter((p) => p.id !== projectToDelete.id)
        );

        setPhotoLocs((prevPhotoLocs) => {
          const newPhotoLocs = { ...prevPhotoLocs };
          delete newPhotoLocs[projectToDelete.id];
          return newPhotoLocs;
        });
      } catch (e) {
        setErr(e.message || "Failed to delete project");
      }
    },
    [token, handleUnauthorized]
  );

  const handleDeleteLayer = useCallback(
    async (layerToDelete, projectId) => {
      if (
        !window.confirm(
          `Are you sure you want to delete the layer "${layerToDelete.layer_name}"?`
        )
      ) {
        return;
      }
      try {
        setErr("");
        const res = await fetch(
          `${API_BASE}/projects/${projectId}/layers/${layerToDelete.id}`,
          {
            method: "DELETE",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.status === 401) {
          await handleUnauthorized(res);
          return;
        }
        if (!res.ok) throw new Error(`Failed to delete layer (${res.status})`);

        setDataLayers((m) => {
          const updatedLayers = (m[projectId] || []).filter(
            (layer) => layer.id !== layerToDelete.id
          );
          return { ...m, [projectId]: updatedLayers };
        });
        onLayersChanged({
          type: "delete",
          layerId: layerToDelete.id,
          projectId,
        });
      } catch (e) {
        setErr(e.message || "Failed to delete layer");
      }
    },
    [token, handleUnauthorized, onLayersChanged]
  );

  const toggleFolder = (projectId, folder) => {
    const key = `${projectId}::${folder}`;
    setOpenFolders((s) => ({ ...s, [key]: !s[key] }));
  };

  const handleSignOut = (e) => {
    e.preventDefault();
    signOut();
  };

  const handleProjectClick = (project) => {
    navigate(`/Project?projectId=${project.id}`);
  };

  const focusLocationOnMap = useCallback(
    (project, location, type = "folder") => {
      const lat = toNumOrNull(location?.lat);
      const lon = toNumOrNull(location?.lon);
      if (lat == null || lon == null) return;

      onMapFocusRequest?.({
        projectId: project.id,
        locationId: location.id,
        lat,
        lon,
        type,
        name:
          location?.folder_name ||
          location?.location_name ||
          project?.name ||
          "Location",
        zoom: 18,
      });

      if (!isProjectPage || currentProjectIdInUrl !== String(project.id)) {
        navigate(`/Project?projectId=${project.id}`);
      }
    },
    [onMapFocusRequest, isProjectPage, currentProjectIdInUrl, navigate]
  );

  const focusPhotoLocationsOnMap = useCallback(
    (project, folderList = []) => {
      const coords = (folderList || [])
        .map((folder) => ({
          lat: toNumOrNull(folder?.lat),
          lon: toNumOrNull(folder?.lon),
        }))
        .filter((point) => point.lat != null && point.lon != null);

      if (coords.length === 0) return;

      onMapFocusRequest?.({
        projectId: project.id,
        type: "photo-locations",
        name: `${project?.name || "Project"} Photo Locations`,
        bounds: coords,
      });

      if (!isProjectPage || currentProjectIdInUrl !== String(project.id)) {
        navigate(`/Project?projectId=${project.id}`);
      }
    },
    [onMapFocusRequest, isProjectPage, currentProjectIdInUrl, navigate]
  );

  const handleFolderCreated = (newFolder) => {
    // Tidak perlu lagi update state lokal, cukup panggil refresh dari parent
    refreshFolders();
    setAddFolderOpen(false); // Tetap tutup modal
  };

  // MODIFIKASI FUNGSI handleDeleteFolder
  const handleDeleteFolder = useCallback(
    async (folderToDelete, projectId) => {
      if (
        !window.confirm(
          `Are you sure you want to delete folder "${folderToDelete.folder_name}"?`
        )
      ) {
        return;
      }
      try {
        setErr("");
        const res = await fetch(
          `${API_BASE}/photo_locations/${folderToDelete.id}`,
          {
            method: "DELETE",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        if (res.status === 401) {
          await handleUnauthorized(res);
          return;
        }
        if (!res.ok) throw new Error(`Failed to delete folder (${res.status})`);

        // GANTI UPDATE STATE LOKAL DENGAN MEMANGGIL REFRESH
        refreshFolders();

      } catch (e) {
        setErr(e.message || "Failed to delete folder");
      }
    },
    [token, handleUnauthorized, refreshFolders]
  );

  // Handler untuk edit folder
  const handleEditFolder = (folder, project) => {
    setEditingFolder({
      ...folder,
      project_id: project.id
    });
    setAddFolderFor(project);
    setAddFolderOpen(true);
  };

  const handleFolderUpdated = (updatedFolder) => {
    // Panggil refresh untuk memastikan semua komponen mendapat data terbaru
    refreshFolders();
    // Anda mungkin masih perlu me-reload photoLocs jika nama folder berubah
    // Cara paling mudah adalah me-reload semua foto untuk proyek tersebut
    const projectToReload = projects.find(p => p.id === updatedFolder.project_id);
    if (projectToReload) {
      loadPhotoLocations(projectToReload);
    }
  };

  const handleAddLayer = (projectId) => {
    setAddLayerFor(projectId);
    setAddLayerOpen(true);
  };

  const handleSaveLayer = useCallback(
    (newLayer, projectId) => {
      setDataLayers((m) => {
        const currentLayers = m[projectId] || [];
        return { ...m, [projectId]: [...currentLayers, newLayer] };
      });
      onLayersChanged({ type: "add", newLayer, projectId });
      setAddLayerOpen(false);
    },
    [onLayersChanged]
  );

  // Handler untuk membuat panorama baru


  // Handler untuk menghapus panorama location
  const handleDeletePanorama = useCallback(
    async (panoToDelete, projectId) => {
      if (!window.confirm(`Are you sure you want to delete panorama "${panoToDelete.location_name}"?`)) {
        return;
      }
      try {
        setErr("");
        const res = await fetch(`${API_BASE}/panorama_locations/${panoToDelete.id}`, {
          method: "DELETE",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.status === 401) {
          await handleUnauthorized(res);
          return;
        }
        if (!res.ok) throw new Error(`Failed to delete panorama (${res.status})`);
        refreshPanoramas();
      } catch (e) {
        setErr(e.message || "Failed to delete panorama");
      }
    },
    [token, handleUnauthorized, refreshPanoramas]
  );

  // Handler untuk edit panorama
  const handleEditPanorama = (panorama, project) => {
    setEditingPanorama({ ...panorama, project_id: project.id });
    setAddPanoramaFor(project);
    setAddPanoramaOpen(true);
  };

  // Handler untuk update panorama setelah edit
  const handlePanoramaUpdated = useCallback((updatedPanorama) => {
    refreshPanoramas();
  }, [refreshPanoramas]);

  // Handler untuk menghapus foto panorama
  const handleDeletePanoramaPhoto = useCallback(
    async (photoId, panoId) => {
      if (!window.confirm("Are you sure you want to delete this panorama photo?")) {
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/panorama_photos/${photoId}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (res.status === 401) {
          await handleUnauthorized(res);
          return;
        }
        if (!res.ok) {
          throw new Error(`Failed to delete panorama photo (${res.status})`);
        }

        setPanoramaPhotos(prev => ({
          ...prev,
          [panoId]: (prev[panoId] || []).filter(p => p.id !== photoId)
        }));
      } catch (e) {
        console.error("Error deleting panorama photo:", e);
        setErr(e.message || "Failed to delete panorama photo");
      }
    },
    [token, handleUnauthorized]
  );

  return (
    <aside className="sidebar nav-dark d-flex flex-column">
      <style>{`
        .nav-dark{--nav-bg:#1f2a3b;--nav-bg-2:#182232;--nav-surface:#24324a;--nav-hover:#2b3c58;--nav-active:#324665;--nav-border:#ffffff14;--nav-text:#e9f1fb;--nav-muted:#a9b8cf;--nav-accent:#6ea8fe;--ring:0 0 0 3px #6ea8fe44;}
        .sidebar{width:300px;position:sticky;top:0;height:100vh;overflow-y:auto;background:linear-gradient(180deg,var(--nav-bg) 0%,var(--nav-bg-2) 100%);color:var(--nav-text);border-right:1px solid var(--nav-border);box-shadow:inset -1px 0 0 rgba(0,0,0,.15);}
        .sidebar::-webkit-scrollbar{width:10px;} .sidebar::-webkit-scrollbar-thumb{background:#ffffff1f;border-radius:6px;} .sidebar::-webkit-scrollbar-thumb:hover{background:#ffffff33;}
        .brand{padding:16px 18px;border-bottom:1px solid var(--nav-border);display:flex;align-items:center;gap:12px;background:linear-gradient(180deg,#223049,transparent);}
        .brand .logo{width:38px;height:38px;border-radius:12px;display:grid;place-items:center;background:#2a3b59;color:var(--nav-text);box-shadow:0 4px 10px rgba(0,0,0,.25), inset 0 1px 0 #ffffff14;}
        .brand .logo i{font-size:18px;} .brand .title{margin:0;font-size:18px;font-weight:700;letter-spacing:.2px;color:var(--nav-text);}
        .section{padding:8px 10px 0;} .section-header{display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:12px;color:var(--nav-text);} .section-header:hover{background:#ffffff0a;}
        .section-title{font-weight:600;display:flex;align-items:center;gap:10px;color:var(--nav-text);} .section-title i{opacity:.9;}
        .chev-btn{margin-left:auto;width:28px;height:28px;border-radius:8px;border:0;background:#ffffff12;display:grid;place-items:center;cursor:pointer;color:var(--nav-text);transition:.18s;}
        .chev-btn:hover{background:#ffffff1f;} .chev-btn:focus-visible{outline:none;box-shadow:var(--ring);}
        .add-btn{margin-left:6px;width:28px;height:28px;border-radius:8px;border:0;background:#2f3f5f;color:#e9f1fb;cursor:pointer;} .add-btn:hover{background:#39507a;} .add-btn:disabled{opacity:.5;cursor:not-allowed;}
        .collapsible[hidden]{display:none !important;}
        .menu-list{padding:2px 6px 10px;} .menu-link{display:flex;align-items:center;gap:12px;width:100%;padding:10px 12px;border-radius:10px;text-decoration:none;color:var(--nav-text);opacity:.92;transition:background .15s ease,opacity .15s ease,transform .04s ease;position:relative;}
        .menu-link:hover{background:var(--nav-hover);opacity:1;} .menu-link.active{background:var(--nav-active);opacity:1;font-weight:600;box-shadow:inset 0 0 0 1px #ffffff1f,0 2px 12px rgba(0,0,0,.25);} .menu-link.active::before{content:"";position:absolute;inset:0 0 0 auto;width:3px;border-radius:3px;background:var(--nav-accent);}
        .divider{border-top:1px solid var(--nav-border);margin:10px;}
        .folder{padding-left:8px;color:var(--nav-muted);font-size:12px;text-transform:uppercase;margin:6px 0 4px;display:flex;align-items:center;gap:8px;}
        .folder-row{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:10px;cursor:pointer;}
        .folder-row:hover{background:var(--nav-hover);}
        .sub-item{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:8px;color:var(--nav-text);font-size:14px;position:relative;} 
        .sub-item:hover{background:var(--nav-hover);}
        .sub-item-link { text-decoration: none; color: inherit; display: block; width: 100%; }
        .sub-item-actions { margin-left: auto; display: flex; gap: 8px; }
        .sub-item-actions button { background: none; border: none; color: inherit; padding: 0; cursor: pointer; transition: color 0.2s ease; }
        .sub-item-actions button:hover { color: var(--nav-accent); }
        .sub-item-actions button.trash:hover { color: #f87171; }
        .check-row{display:flex;align-items:center;gap:8px;padding:8px 10px;color:var(--nav-text);}
        .sidebar-footer{margin-top:auto;padding:12px;border-top:1px solid var(--nav-border);background:#1a2333;}
        .user-tile{width:100%;display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:12px;background:var(--nav-surface);border:1px solid var(--nav-border);}
        .user-avatar{width:34px;height:34px;border-radius:50%;background:#33425f;color:#eaf2ff;display:grid;place-items:center;font-weight:700;overflow:hidden;font-size:13px;box-shadow:inset 0 1px 0 #ffffff1a;}
        .user-avatar img{width:100%;height:100%;object-fit:cover;border-radius:50%;}
        .user-meta .name{font-weight:700;color:var(--nav-text);line-height:1.1;} .user-meta .email{color:var(--nav-muted);font-size:12px;}
        .add-photo-tile{
          width:calc(100% - 20px);
          margin:8px 10px 0 10px;
          padding:10px 12px;
          border:1px dashed #ffffff44;
          border-radius:10px;
          background:transparent;
          color:var(--nav-text);
          display:flex;align-items:center;justify-content:center;gap:6px;
          cursor:pointer;
        }
        .add-photo-tile:hover{ background:#ffffff0a; border-color:#ffffff66; }
        
        .delete-project-btn {
          opacity: 0;
          transition: opacity 0.2s ease-in-out;
        }
        .menu-link:hover .delete-project-btn {
          opacity: 1;
        }

        .photo-actions-container {
            display: flex;
            gap: 8px;
            visibility: hidden;
            opacity: 0;
            transition: visibility 0s, opacity 0.2s ease-in-out;
        }
        .sub-item:hover .photo-actions-container {
            visibility: visible;
            opacity: 1;
        }
      `}</style>

      {/* Brand */}
      <div className="brand">
        <div className="logo">
          <i className="bi bi-airplane-engines-fill" />
        </div>
        <h1 className="title">Terra Progress</h1>
      </div>

      {/* Sections */}
      <div className="section">
        <ul className="list-unstyled ps-0 m-0">
          {/* Home */}
          <li className="mb-1">
            <div className="section-header" aria-label="Home section header">
              <span className="section-title">
                <i className="bi bi-house-door" /> Home
              </span>
              <button
                type="button"
                className="chev-btn"
                aria-expanded={homeOpen}
                aria-controls="home-collapse"
                onClick={() => setHomeOpen((v) => !v)}
                aria-label={homeOpen ? "Collapse Home" : "Expand Home"}
                title={homeOpen ? "Collapse" : "Expand"}
              >
                <ChevronRight
                  size={16}
                  style={{
                    transform: homeOpen ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform .18s ease",
                  }}
                />
              </button>
            </div>

            <div id="home-collapse" className="collapsible" hidden={!homeOpen}>
              <ul className="menu-list list-unstyled fw-normal">
                <li>
                  <NavLink
                    to="/Homepage"
                    end
                    className={({ isActive }) =>
                      `menu-link ${isActive ? "active" : ""}`
                    }
                  >
                    <i className="bi bi-grid-1x2" />
                    <span>Homepage</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/Updates"
                    className={({ isActive }) =>
                      `menu-link ${isActive ? "active" : ""}`
                    }
                  >
                    <i className="bi bi-bell" />
                    <span>Updates</span>
                  </NavLink>
                </li>
              </ul>
            </div>
          </li>

          {/* Projects */}
          <li className="mb-1">
            <div
              className="section-header"
              aria-label="Projects section header"
            >
              <span
                className="section-title"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/Project")}
                title="View all projects on map"
              >
                <i className="bi bi-diagram-3-fill" /> Projects
              </span>
              <button
                type="button"
                className="add-btn"
                title={ORG ? "Add Project" : "No organization detected"}
                aria-label="Add Project"
                onClick={() => ORG && setAddProjectOpen(true)}
                disabled={!ORG}
              >
                <i className="bi bi-plus-lg" />
              </button>
              <button
                type="button"
                className="chev-btn"
                aria-expanded={projectsOpen}
                aria-controls="projects-collapse"
                onClick={() => setProjectsOpen((v) => !v)}
                aria-label={
                  projectsOpen ? "Collapse Projects" : "Expand Projects"
                }
                title={projectsOpen ? "Collapse" : "Expand"}
              >
                <ChevronRight
                  size={16}
                  style={{
                    transform: projectsOpen ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform .18s ease",
                  }}
                />
              </button>
            </div>

            {!ORG && (
              <div className="px-3 small text-warning mt-1">
                Sign in again to load your organization before adding projects.
              </div>
            )}
            <div
              className="px-3 small"
              style={{ color: "#a9b8cf" }}
              hidden={!err}
              aria-live="polite"
            >
              {err}
            </div>

            <div
              id="projects-collapse"
              className="collapsible"
              hidden={!projectsOpen}
            >
              <ul className="menu-list list-unstyled fw-normal">
                {loading && (
                  <li className="px-3 py-2" style={{ color: "#a9b8cf" }}>
                    Loading projects…
                  </li>
                )}
                {!loading && projects.length === 0 && (
                  <li className="px-3 py-2" style={{ color: "#a9b8cf" }}>
                    No projects yet. Click “+” to add.
                  </li>
                )}

                {projects.map((p) => {
                  const groupedPhotos = photoLocs[p.id] || {};
                  const isProjectActive =
                    currentProjectIdInUrl === p.id.toString();
                  const photoLocsOpen = openFolders[`${p.id}::photo-locs`];
                  const dataLayersOpen = openFolders[`${p.id}::data-layers`];
                  const panoramasOpen = openFolders[`${p.id}::panoramas`];

                  return (
                    <li key={p.id} className="mb-1">
                      <div
                        className={`menu-link ${isProjectActive ? "active" : ""
                          }`}
                        onClick={() => handleProjectClick(p)}
                        onMouseEnter={() => setHoveredProject(p.id)}
                        onMouseLeave={() => setHoveredProject(null)}
                      >
                        <i className="bi bi-building-fill" />
                        <span className="fw-semibold me-auto">{p.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link trash delete-project-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(p);
                          }}
                          title={`Delete project "${p.name}"`}
                          style={{
                            opacity: hoveredProject === p.id ? 1 : 0,
                          }}
                        >
                          <Trash size={14} />
                        </button>
                      </div>

                      {isProjectActive && (
                        <div className="ps-3">
                          {/* Photo Locations Section */}
                          <div
                            className="folder-row"
                            onClick={() => {
                              toggleFolder(p.id, "photo-locs");
                              focusPhotoLocationsOnMap(p, folders[p.id] || []);
                            }}
                          >
                            <ChevronRight
                              size={16}
                              style={{
                                transform: photoLocsOpen ? "rotate(90deg)" : "rotate(0deg)",
                              }}
                            />
                            <i className="bi bi-folder2-open" />
                            <span>Photo Locations</span>
                            <button
                              className="add-btn ms-auto"
                              title="Add Folder"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAddFolderFor(p);
                                setAddFolderOpen(true);
                              }}
                            >
                              <i className="bi bi-plus-lg" />
                            </button>
                          </div>

                          <div hidden={!photoLocsOpen} className="ps-4">
                            {foldersLoading ? (
                              <div className="px-2 py-1 small" style={{ color: "#a9b8cf" }}>
                                Loading folders...
                              </div>
                            ) : (
                              (folders[p.id] || []).map((folder) => {
                                const folderPhotos = (groupedPhotos[folder.folder_name] || []);

                                return (
                                  <div key={folder.id} className="mb-1">
                                    <div
                                      className="folder-row"
                                      onClick={() => {
                                        toggleFolder(p.id, `folder-${folder.id}`);
                                        focusLocationOnMap(p, folder, "folder");
                                      }}
                                    >
                                      <ChevronRight
                                        size={16}
                                        style={{
                                          transform: openFolders[`${p.id}::folder-${folder.id}`]
                                            ? "rotate(90deg)"
                                            : "rotate(0deg)",
                                        }}
                                      />
                                      <i className="bi bi-folder" />
                                      <span className="fw-semibold me-auto">
                                        {folder.folder_name}
                                      </span>

                                      <div className="sub-item-actions">
                                        <button
                                          type="button"
                                          title="Edit folder"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditFolder(folder, p);
                                          }}
                                        >
                                          <i className="bi bi-pencil" />
                                        </button>
                                        <button
                                          type="button"
                                          className="trash"
                                          title="Delete folder"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteFolder(folder, p.id);
                                          }}
                                        >
                                          <Trash size={14} />
                                        </button>
                                      </div>
                                    </div>

                                    <div hidden={!openFolders[`${p.id}::folder-${folder.id}`]} className="ps-4">
                                      {folderPhotos.map((ph) => (
                                        <div
                                          key={ph.id}
                                          className="sub-item"
                                          onMouseEnter={() =>
                                            setHoveredPhoto(ph.id)
                                          }
                                          onMouseLeave={() =>
                                            setHoveredPhoto(null)
                                          }
                                        >
                                          <span className="text-truncate">
                                            {ph.photo_name}
                                          </span>
                                          {hoveredPhoto === ph.id && (
                                            <div className="sub-item-actions">
                                              {ph.file_url && (
                                                <button
                                                  type="button"
                                                  title="View photo"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(
                                                      `/Project?projectId=${p.id
                                                      }&folder=${encodeURIComponent(
                                                        folder.folder_name
                                                      )}&photoId=${ph.id}`
                                                    );
                                                  }}
                                                >
                                                  <i className="bi bi-eye" />
                                                </button>
                                              )}
                                              <button
                                                type="button"
                                                className="trash"
                                                title="Delete photo"
                                                onClick={() =>
                                                  handleDeletePhoto(ph)
                                                }
                                              >
                                                <Trash size={14} />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      ))}

                                      {folderPhotos.length === 0 && (
                                        <div
                                          className="px-2 py-1 small"
                                          style={{ color: "#a9b8cf" }}
                                        >
                                          No photos in this folder.
                                        </div>
                                      )}

                                      <button
                                        type="button"
                                        className="add-photo-tile"
                                        onClick={() =>
                                          setAddPhotoFor({
                                            project: p,
                                            initialFolder: folder.folder_name,
                                          })
                                        }
                                      >
                                        <i className="bi bi-plus-lg me-2" />
                                        Add Photo
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            )}

                            {(folders[p.id] || []).length === 0 && !foldersLoading[p.id] && (
                              <div className="px-2 py-1 small" style={{ color: "#a9b8cf" }}>
                                No folders yet. Click "+" to add.
                              </div>
                            )}
                          </div>

                          {/* Panorama Locations Section */}
                          <div
                            className="folder-row mt-1"
                            onClick={() => toggleFolder(p.id, "panoramas")}
                          >
                            <ChevronRight
                              size={16}
                              style={{
                                transform: panoramasOpen
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                              }}
                            />
                            <i className="bi bi-globe" />
                            <span>Panorama Locations</span>
                            <button
                              className="add-btn ms-auto"
                              title="Add Panorama Location"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAddPanoramaFor(p);
                                setEditingPanorama(null);
                                setAddPanoramaOpen(true);
                              }}
                            >
                              <i className="bi bi-plus-lg" />
                            </button>
                          </div>

                          <div hidden={!panoramasOpen} className="ps-4">
                            {panoramasLoading[p.id] ? (
                              <div className="px-2 py-1 small" style={{ color: "#a9b8cf" }}>
                                Loading panoramas...
                              </div>
                            ) : (
                              (panoramas[p.id] || []).map((pano) => {
                                const panoPhotos = panoramaPhotos[pano.id] || [];

                                return (
                                  <div key={pano.id} className="mb-1">
                                    <div
                                      className="folder-row"
                                      onClick={() => {
                                        toggleFolder(p.id, `pano-folder-${pano.id}`);
                                        loadPanoramaPhotos(pano.id);
                                        focusLocationOnMap(p, pano, "panorama");
                                      }}
                                    >
                                      <ChevronRight
                                        size={16}
                                        style={{
                                          transform: openFolders[`${p.id}::pano-folder-${pano.id}`]
                                            ? "rotate(90deg)"
                                            : "rotate(0deg)",
                                        }}
                                      />
                                      <i className="bi bi-folder" />
                                      <span className="fw-semibold me-auto">
                                        {pano.location_name}
                                      </span>

                                      <div className="sub-item-actions">
                                        <button
                                          type="button"
                                          title="Edit folder"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditPanorama(pano, p);
                                          }}
                                        >
                                          <i className="bi bi-pencil" />
                                        </button>
                                        <button
                                          type="button"
                                          className="trash"
                                          title="Delete folder"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeletePanorama(pano, p.id);
                                          }}
                                        >
                                          <Trash size={14} />
                                        </button>
                                      </div>
                                    </div>

                                    <div hidden={!openFolders[`${p.id}::pano-folder-${pano.id}`]} className="ps-4">
                                      {panoPhotos.map((photo) => (
                                        <div
                                          key={photo.id}
                                          className="sub-item"
                                          onMouseEnter={() => setHoveredPhoto(photo.id)}
                                          onMouseLeave={() => setHoveredPhoto(null)}
                                        >
                                          <span className="text-truncate">{photo.photo_name}</span>
                                          {hoveredPhoto === photo.id && (
                                            <div className="sub-item-actions">
                                              {photo.file_url && (
                                                <button
                                                  type="button"
                                                  title="View 360"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(
                                                      `/Project?projectId=${p.id}&panoramaId=${pano.id}&photoId=${photo.id}&mode=panorama`
                                                    );
                                                  }}
                                                >
                                                  <i className="bi bi-eye" />
                                                </button>
                                              )}
                                              <button
                                                type="button"
                                                className="trash"
                                                title="Delete photo"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeletePanoramaPhoto(photo.id, pano.id);
                                                }}
                                              >
                                                <Trash size={14} />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      ))}

                                      {panoPhotos.length === 0 && (
                                        <div className="px-2 py-1 small" style={{ color: "#a9b8cf" }}>
                                          No photos in this location.
                                        </div>
                                      )}

                                      {/* Add Photo Button - sama seperti Photo Locations */}
                                      <button
                                        type="button"
                                        className="add-photo-tile"
                                        onClick={() =>
                                          setUploadPanoramaFor({ project: p, panorama: pano })
                                        }
                                      >
                                        <i className="bi bi-plus-lg me-2" />
                                        Add Panorama
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                            {(panoramas[p.id] || []).length === 0 &&
                              !panoramasLoading[p.id] && (
                                <div className="px-2 py-1 small" style={{ color: "#a9b8cf" }}>
                                  No panoramas yet.
                                </div>
                              )}
                          </div>

                          {/* Data Layers Section */}
                          <div
                            className="folder-row"
                            onClick={() => toggleFolder(p.id, "data-layers")}
                          >
                            <ChevronRight
                              size={16}
                              style={{
                                transform: dataLayersOpen
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                              }}
                            />
                            <i className="bi bi-layers" />
                            <span>Data Layers</span>
                            {dataLayersOpen && (
                              <button
                                className="add-btn ms-auto"
                                title="Add Layer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddLayer(p.id);
                                }}
                              >
                                <i className="bi bi-plus-lg" />
                              </button>
                            )}
                          </div>

                          <div hidden={!dataLayersOpen} className="ps-4">
                            {layersLoading[p.id] ? (
                              <div
                                className="px-2 py-1 small"
                                style={{ color: "#a9b8cf" }}
                              >
                                Loading layers...
                              </div>
                            ) : (
                              (dataLayers[p.id] || []).map((layer) => (
                                <div
                                  key={layer.id}
                                  className="sub-item"
                                  onMouseEnter={() => setHoveredLayer(layer.id)}
                                  onMouseLeave={() => setHoveredLayer(null)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {/* Nama layer di kiri */}
                                  <span className="text-truncate me-auto">
                                    {layer.layer_name}
                                  </span>
                                  {/* Checkbox & Trash hanya saat hover */}
                                  {hoveredLayer === layer.id && (
                                    <div
                                      className="sub-item-actions"
                                      style={{ display: "flex", gap: 8 }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!!activeLayerIds[layer.id]}
                                        onChange={() =>
                                          toggleLayerVisibility(layer.id)
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ cursor: "pointer" }}
                                      />
                                      <button
                                        type="button"
                                        className="trash"
                                        title="Delete layer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteLayer(layer, p.id);
                                        }}
                                        style={{
                                          color: "#f87171",
                                          background: "none",
                                          border: "none",
                                          padding: 0,
                                          cursor: "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Trash size={14} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                            {(dataLayers[p.id] || []).length === 0 &&
                              !layersLoading[p.id] && (
                                <div
                                  className="px-2 py-1 small"
                                  style={{ color: "#a9b8cf" }}
                                >
                                  No layers.
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </li>
          <li className="divider" />
        </ul>
      </div>

      {/* Footer: User */}
      <div className="sidebar-footer">
        <div className="user-tile">
          <div className="user-avatar">
            {profileImg ? <img src={profileImg} alt="Profile" /> : initials}
          </div>
          <div className="user-meta">
            <div className="name">{displayName}</div>
            <div className="email">{displayEmail}</div>
          </div>
          <button
            type="button"
            className="chev-btn ms-auto"
            aria-expanded={userOpen}
            aria-controls="user-collapse"
            onClick={() => setUserOpen((v) => !v)}
            aria-label={userOpen ? "Collapse user menu" : "Expand user menu"}
            title={userOpen ? "Collapse" : "Expand"}
          >
            <ChevronRight
              size={16}
              style={{
                transform: userOpen ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform .18s ease",
              }}
            />
          </button>
        </div>

        <div id="user-collapse" className="user-menu mt-2" hidden={!userOpen}>
          <div className="list-group">
            <NavLink
              to="/Profile"
              className="list-group-item list-group-item-action"
            >
              <i className="bi bi-person" /> Profile
            </NavLink>
            <NavLink
              to="/Settings"
              className="list-group-item list-group-item-action"
            >
              <i className="bi bi-gear" /> Settings
            </NavLink>
            <button
              type="button"
              className="list-group-item list-group-item-action text-danger"
              onClick={handleSignOut}
            >
              <i className="bi bi-box-arrow-right" /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddProjectModal
        open={addProjectOpen}
        onClose={() => setAddProjectOpen(false)}
        onCreate={handleCreateProject}
        org={ORG}
      />

      <FolderModal
        open={addFolderOpen}
        onClose={() => {
          setAddFolderOpen(false);
          setEditingFolder(null); // Reset editing state saat modal ditutup
        }}
        project={addFolderFor}
        org={ORG}
        token={token}
        apiBase={API_BASE}
        onFolderCreated={handleFolderCreated} // Gunakan fungsi baru
        onFolderUpdated={handleFolderUpdated}
        editingFolder={editingFolder}
      />

      {/* Modal baru untuk Panorama */}
      <PanoramaModal
        open={addPanoramaOpen}
        onClose={() => {
          setAddPanoramaOpen(false);
          setEditingPanorama(null);
        }}
        onSave={(savedPano) => {
          refreshPanoramas();
          setAddPanoramaOpen(false);
        }}
        project={addPanoramaFor}
        token={token}
        apiBase={API_BASE}
        editingPanorama={editingPanorama}
      />

      {/* Modal untuk Unggah Foto Panorama */}
      <UploadPanoramaModal
        open={!!uploadPanoramaFor}
        onClose={() => setUploadPanoramaFor(null)}
        project={uploadPanoramaFor?.project}
        panorama={uploadPanoramaFor?.panorama}
        token={token}
        apiBase={API_BASE}
        onUploaded={(created, panoId) => {
          setPanoramaPhotos(prev => ({
            ...prev,
            [panoId]: [...(prev[panoId] || []), created]
          }));
          refreshPanoramas();
          onPhotoDataChanged();
        }}
      />

      <UploadPhotosModal
        open={!!addPhotoFor}
        onClose={() => setAddPhotoFor(null)}
        project={addPhotoFor?.project}
        org={ORG}
        token={token}
        apiBase={API_BASE}
        initialFolder={addPhotoFor?.initialFolder || ""}
        allFolders={folders}
        onUploaded={(created, projectId) => {
          setPhotoLocs((m) => {
            const current = m[projectId] || {};
            const folder = (created.photo_folder || "(Uncategorized)").trim();
            const list = current[folder] || [];
            return {
              ...m,
              [projectId]: { ...current, [folder]: [created, ...list] },
            };
          });
          onPhotoDataChanged();
        }}
      />

      <ImageModal
        open={!!imageViewFor}
        onClose={() => setImageViewFor(null)}
        src={imageViewFor?.file_url}
      />

      <AddLayerModal
        open={addLayerOpen}
        onClose={() => setAddLayerOpen(false)}
        onSave={(newLayer) => {
          loadDataLayers(addLayerFor);
          onLayersChanged({ type: "add", newLayer, projectId: addLayerFor });
          setAddLayerOpen(false);
        }}
        projectId={addLayerFor}
        token={token}
        apiBase={API_BASE}
      />
    </aside>
  );
}





